import { memo, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import type { FileRef } from '../content/types';
import { useContent } from './ContentContext';

interface Props {
  file: FileRef | null | undefined;
  lqip?: string;
  fit?: 'cover' | 'contain';
  className?: string;
  style?: CSSProperties;
  imgStyle?: CSSProperties;
  alt?: string;
}

/**
 * Imagen local con LQIP debajo. La LQIP (24 px, base64 en el manifiesto) se pinta en el mismo
 * frame en que se monta el componente, así nunca hay un hueco vacío; la imagen real aparece
 * encima cuando termina de decodificar. Si ya estaba decodificada (caso normal) no hay fundido.
 */
export const SmartImg = memo(function SmartImg({ file, lqip, fit = 'cover', className = '', style, imgStyle, alt = '' }: Props) {
  const { content, reportMissing } = useContent();
  const src = content.src(file);
  const ref = useRef<HTMLImageElement>(null);
  const [ready, setReady] = useState(false);
  const [instant, setInstant] = useState(true);

  useLayoutEffect(() => {
    const img = ref.current;
    const already = !!img && img.complete && img.naturalWidth > 0;
    setReady(already);
    setInstant(already);
  }, [src]);

  return (
    <div
      className={`overflow-hidden ${className || 'relative'}`}
      style={{
        backgroundColor: 'rgb(var(--c-placeholder))',
        backgroundImage: lqip ? `url("${lqip}")` : undefined,
        backgroundSize: fit,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        ...style,
      }}
    >
      {src ? (
        <img
          ref={ref}
          src={src}
          alt={alt}
          draggable={false}
          decoding="async"
          onLoad={() => setReady(true)}
          onError={() => file && reportMissing(file.key)}
          className="fill h-full w-full"
          style={{
            objectFit: fit,
            opacity: ready ? 1 : 0,
            transition: instant ? 'none' : 'opacity 180ms ease',
            ...imgStyle,
          }}
        />
      ) : null}
    </div>
  );
});
